import * as cheerio from "cheerio";

export const formDataRichText = async (
  htmlList: string[],
  formData: FormData,
) => {
  if (!htmlList || htmlList.length === 0) {
    return; // Handle empty list case
  }

  const f = async (prefix: string, html: string) => {
    formData.append(`${prefix}.richtext`, JSON.stringify(htmlToJson(html)));
    const files = await extractAndDownloadBlobImagesFromHTML(html);

    let index = 0;
    files.forEach((file) =>
      formData.append(`${prefix}.files[${index++}].file`, file),
    );

    const mediaFileIds = getRemainsMediaFileIds(html) ?? [];
    for (const mediaFileId of mediaFileIds) {
      formData.append(`${prefix}.files[${index++}].mediaFileId`, mediaFileId);
    }
    return formData;
  };

  if (htmlList.length === 1) {
    const html = htmlList[0];
    return await f("richtext", html);
  }

  await Promise.all(
    htmlList.map(async (html, index) => f(`richtext[${index}]`, html)),
  );
  return formData;
};

export function htmlToJson(html: string): any {
  const $ = cheerio.load(html);

  /**
   * Recursive function to process a node into a JSON object.
   */
  function processNode(node: any): any {
    const obj: any = {
      type: node.type,
      name: node.tagName,
      attributes: node.attribs || {},
      children: [] as any[],
      text: node.type === "text" ? $(node).text() : undefined,
    };

    if (node.type === "tag" && node.children) {
      node.children.forEach((child: any) => {
        obj.children.push(processNode(child));
      });
    }

    // Clean up empty fields
    // if (obj.text === "") delete obj.text;
    // if (!obj.children.length) delete obj.children;
    if (
      obj.name === "img" &&
      obj.attributes.src &&
      obj.attributes.src.startsWith("blob:")
    ) {
      obj.attributes.src = "MULTIPARTIFLE677CA81B0C9C80078555818CB5F63C3B";
    }

    return obj;
  }

  const rootNodes = $.root().children().toArray();
  return rootNodes.map(processNode);
}

export function richtextToHtml(string?: string): string {
  return jsonToHtml(string ? JSON.parse(string) : []);
}
/**
 * Converts a JSON object back to an HTML string using json2html.
 * @param json - The input JSON object.
 * @returns HTML string.
 */
type HtmlNode = {
  type: "text" | "tag";
  name?: string;
  text?: string;
  attributes?: Record<string, string>;
  children?: HtmlNode[];
};

export function jsonToHtml(json: HtmlNode[]): string {
  const selfClosingTags = new Set(["br", "img", "hr", "input", "meta", "link"]);

  const escapeAttr = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");

  const transform = (node: HtmlNode): string => {
    if (node.type === "text") {
      return node.text || "";
    }

    const { name = "", attributes, children } = node;
    const attrs =
      attributes &&
      Object.entries(attributes)
        .map(([key, value]) => `${key}="${escapeAttr(value)}"`)
        .join(" ");

    if (selfClosingTags.has(name)) {
      return `<${name}${attrs ? " " + attrs : ""} />`;
    }

    const openTag = `<${name}${attrs ? " " + attrs : ""}>`;
    const closeTag = `</${name}>`;
    const innerContent = children ? children.map(transform).join("") : "";

    return `${openTag}${innerContent}${closeTag}`;
  };

  return json.map(transform).join("");
}

async function extractAndDownloadBlobImagesFromHTML(
  htmlString: string,
): Promise<File[]> {
  let fileData: File[] = [];
  // Parse the HTML string into a Document object
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  // Get all image elements from the parsed HTML
  const images: NodeListOf<HTMLImageElement> = doc.querySelectorAll("img");

  // Filter images with a 'src' attribute that starts with 'blob:'
  const blobImages: HTMLImageElement[] = Array.from(images).filter((img) =>
    img.src.startsWith("blob:"),
  );

  // Extract the URLs of these blob images
  const blobUrls: string[] = blobImages.map((img) => img.src);

  // Download each blob image
  for (const [index, url] of blobUrls.entries()) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error(
          `Failed to fetch blob image at ${url}: ${response.status} ${response.statusText}`,
        );
        continue;
      }
      const blob = await response.blob();
      const file = new File([blob], `blob_image_${index + 1}.png`, {
        type: blob.type,
      });
      fileData.push(file);
    } catch (error) {
      console.error(`Error processing blob image at ${url}`, error);
    }
  }
  return fileData;
}

export const getRemainsMediaFileIds = (s: string): string[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(s, "text/html");
  const currentImages = Array.from(doc.querySelectorAll("img")).map(
    (img) => img.getAttribute("src") || "",
  );
  return currentImages.map(validateAndExtractUUID).filter(Boolean) as string[];
};

function validateAndExtractUUID(url: string): string | null {
  // Regex to check if the URL starts with https:// and extract the UUID
  const regex = /^https:\/\/.*?([a-f0-9]{32})\//;

  // Execute the regex match
  const match = url.match(regex);

  // If a match is found, return the UUID
  if (match && match[1]) {
    return match[1];
  }

  // Return null if no valid UUID is found
  return null;
}
